/**
 * What the ground is made of.
 *
 * Surfaces are the cheapest variety in the game: the same route, driven twice, feels different
 * because part of it is mud. They also carry the loop's best moment - mud dirties the car she
 * coloured, and driving through water washes it clean, which ties her drawing to the simulation in a
 * way she can see and *cause*.
 *
 * Every multiplier here stays on the forgiving side of realistic. Ice that actually behaved like ice
 * would be funny for an adult and a dead end for a three-year-old.
 */

export const SURFACE_IDS = ['road', 'grass', 'mud', 'sand', 'ice', 'water'] as const;
export type SurfaceId = (typeof SURFACE_IDS)[number];

export interface SurfaceDef {
  readonly id: SurfaceId;
  /** Multiplies wheel grip. Below 1 slips, above 1 bites. */
  readonly frictionMul: number;
  /** Resistance that scales with speed, in units of force per m/s. Mud drags, road does not. */
  readonly rollingDrag: number;
  /** Multiplies available drive torque. Sand robs power without making her slide. */
  readonly torqueMul: number;
  /** Dirt picked up per second while driving on it, 0 to 1 of a full coat. */
  readonly dirtGain: number;
  /** Dirt washed off per second. Only water cleans. */
  readonly dirtWash: number;
  /** Ground fill colour. */
  readonly earth: number;
  /** Surface crust colour - the stripe along the very top of the ground. */
  readonly crust: number;
}

export const SURFACES: Readonly<Record<SurfaceId, SurfaceDef>> = {
  road: {
    id: 'road',
    frictionMul: 1,
    rollingDrag: 0,
    torqueMul: 1,
    dirtGain: 0,
    dirtWash: 0,
    earth: 0x6b6f76,
    crust: 0x3f434a,
  },
  grass: {
    id: 'grass',
    frictionMul: 0.95,
    rollingDrag: 0.6,
    torqueMul: 1,
    dirtGain: 0,
    dirtWash: 0,
    earth: 0x7a5b3c,
    crust: 0x5aa02c,
  },
  mud: {
    id: 'mud',
    frictionMul: 0.62,
    rollingDrag: 2.4,
    torqueMul: 0.85,
    // A couple of seconds of mud gives a visibly filthy truck: the payoff has to be quick enough
    // that she connects the mud with the mess.
    dirtGain: 0.5,
    dirtWash: 0,
    earth: 0x5a4630,
    crust: 0x6b4f2e,
  },
  sand: {
    id: 'sand',
    frictionMul: 0.85,
    rollingDrag: 1.6,
    torqueMul: 0.8,
    dirtGain: 0.12,
    dirtWash: 0,
    earth: 0xc9a86a,
    crust: 0xe0c48c,
  },
  ice: {
    id: 'ice',
    // Slippery enough to be funny, nowhere near slippery enough to strand her.
    frictionMul: 0.35,
    rollingDrag: 0,
    torqueMul: 0.9,
    dirtGain: 0,
    dirtWash: 0.15,
    earth: 0x9fc4d8,
    crust: 0xdff1fa,
  },
  water: {
    id: 'water',
    frictionMul: 0.8,
    rollingDrag: 2.0,
    torqueMul: 0.9,
    dirtGain: 0,
    // Faster than mud dirties, so one puddle always fully cleans one muddy stretch. Getting clean
    // must be easier than getting dirty, or the truck ends up permanently brown.
    dirtWash: 0.9,
    earth: 0x3f7fa6,
    crust: 0x66b6dd,
  },
};

export interface SurfaceSegment {
  readonly fromM: number;
  readonly toM: number;
  readonly surface: SurfaceId;
}

/**
 * The surface at a point. Segments win over the default in the order given, so overlapping segments
 * resolve to the first match rather than being an error - route data should never be able to make the
 * ground undefined.
 */
export function surfaceAt(
  segments: readonly SurfaceSegment[],
  x: number,
  fallback: SurfaceId = 'grass',
): SurfaceId {
  for (const segment of segments) {
    if (x >= segment.fromM && x < segment.toM) return segment.surface;
  }
  return fallback;
}

export function surfaceDefAt(
  segments: readonly SurfaceSegment[],
  x: number,
  fallback: SurfaceId = 'grass',
): SurfaceDef {
  return SURFACES[surfaceAt(segments, x, fallback)];
}

/**
 * Advance the dirt coating. Returns the new amount, 0 (spotless) to 1 (filthy).
 *
 * Clamped rather than accumulated without limit, so a long muddy route cannot leave the car so brown
 * that her colours are gone entirely - the point is that it is *her* car, dirty.
 */
export function updateDirt(current: number, surface: SurfaceDef, dt: number): number {
  const next = current + (surface.dirtGain - surface.dirtWash) * dt;
  return Math.max(0, Math.min(1, next));
}
